'use client';

/**
 * **The attribute treatment's header-mounted delete, with one owner**
 * (VALE-75 / [INVDRAW-4]; `docs/attribute-treatment.md` §2 and §6.2).
 *
 * ## Why this exists
 *
 * It is the *bind to the source of truth* rule (root `CLAUDE.md`) applied to the
 * one remaining piece of this treatment that had none. LF-6307 introduced the
 * header delete and LF-6350 / [ATTR-8] restored its measured geometry — but as
 * **hand-typed classes inside `contact-details-drawer.tsx`**. One consumer with
 * hand-typed chrome is a private decision; two would be a divergence, and the
 * inventory drawer is the second. Both now render through this, so the numbers
 * below cannot drift apart.
 *
 * ## The numbers, and why each one is a number rather than a preference
 *
 * With no Save button the bottom of the form stops being a destination, so a
 * destructive action parked there is both hard to find on purpose and easy to
 * hit by accident (§2). Moving it to the header put it in the corner the cursor
 * is thrown at constantly, which is the problem the geometry solves:
 *
 * · **A 32px hit box** (`size="target-32"`, `buttonVariants`' one size step
 *   whose height is a literal `h-[32px]` rather than a `--spacing`-scaled
 *   `h-N` — see its doc in `components/ui/button.tsx` for why: this box must
 *   not shrink when the app's density knob does) and a **~40px gap** to the
 *   Sheet's own close `×`. The bare-icon version measured **23.1px
 *   centre-to-centre against the 24px floor** for targets under 24px — WCAG
 *   2.2 SC 2.5.8 (AA). It failed,
 *   narrowly, and measurement is the only reason anyone would know. The deeper
 *   problem was frequency rather than compliance: a drawer closed dozens of
 *   times a day, next to a record deleted maybe weekly. One misfire and you
 *   start *aiming* at the close button, which taxes the commonest gesture in the
 *   panel forever.
 * · **An icon *and* a label.** At 14px a bare trash and a bare `×` are two small
 *   glyphs in one cluster, and the eye scans shape before meaning.
 * · **Neutral at rest, red on hover and focus.** Permanently-red chrome pulls the
 *   eye on every open and competes with the record, which is the whole point of
 *   the treatment. Solid red is reserved for the confirm button inside the
 *   popover, where it is the action rather than the doorway.
 * · **`px-2`, not `px-2.5`.** The gap is measured from this button's right edge,
 *   and the wider inset ate 1.7px of the separation {@link
 *   ATTRIBUTE_DRAWER_HEADER_ROW} is paying for.
 *
 * Both distances are asserted on the rendered surface in
 * `e2e/lf6307-header-mounted-delete.spec.ts`, not assumed — which is what makes
 * moving them here safe rather than hopeful.
 *
 * ## The confirmation is the light popover, and that is a recorded decision
 *
 * `DeleteConfirmPopover`, not `DeleteConfirmationDialog`. The registry names the
 * dialog as the default and the popover as the exception carrying the narrower
 * test — *the record already open, on a surface that is already an editor, with
 * failure reportable by the caller* (`.claude/docs/standardised-components.md`).
 * Every converted attribute drawer meets all three by construction: the record is
 * what the reader is looking at, the surface is an editor with no read mode, and
 * the host owns the delete and toasts its own failures.
 *
 * §6.2 records why the dialog is wrong *here* specifically: it is a centred modal,
 * so it blacks out the very record the question is about — a heavier gesture than
 * the one being confirmed on a surface whose whole argument is that it reads as a
 * quiet record. LF-6307 shipped the popover **with** its registry row, which is
 * the resolution §6.2 asked for; a surface that reached for the dialog now would
 * be the odd one out rather than the conservative choice.
 */

import { Trash2Icon } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { DeleteConfirmPopover } from '@/components/ui/delete-confirm-popover';

/**
 * The Sheet `className` that lifts its own close `×` onto the header row's line.
 *
 * `SheetHeader` insets its content `pt-8` (27.2px) while `SheetContent`
 * hard-codes its close `×` at `top-4`, so on the drawer as first shipped the
 * Delete button's centre sat at **41.2px against the close button's 20.4px** —
 * two controls in the same corner on two different lines, which reads as a
 * mistake rather than as a pair.
 *
 * The `×` takes no prop (`SheetContent` draws it), but it renders as a *direct
 * child* of the element this lands on, so `[&>button]` reaches it without
 * touching the shared component. The arithmetic, so it stays checkable rather
 * than becoming a magic number: the `×` is a 13.6px box, so `top-[24px]` puts its
 * centre at **30.8px** — and {@link ATTRIBUTE_DRAWER_HEADER_ROW} meets it from
 * the other side at 30.2px.
 *
 * Attribute path only. A flag-off drawer has no header action, so moving its
 * close button would be a change with nothing to gain.
 */
export const ATTRIBUTE_DRAWER_CLOSE_OFFSET = '[&>button]:top-[24px]';

/**
 * The header row that carries a title cluster and {@link AttributeDrawerDelete}.
 *
 * Two measured parts, and **no `items-*`** — alignment is composition rather than
 * measurement, so each surface adds its own (`items-center` for a single-line
 * title, `items-start` where the cluster stacks).
 *
 * · **`-mt-[13px]`** meets the close button's 30.8px from below: `SheetHeader`'s
 *   `pt-8` is a 27.2px inset, less 13px is a 14.2px top, plus half of the 32px
 *   Delete box is **30.2px**. Two controls 0.6px apart, against the 20.8px they
 *   shipped at.
 * · **`pr-14`** reserves the corner the close `×` occupies, so Delete stops ~40px
 *   short of it rather than becoming its neighbour. It was `pr-12`, which
 *   measured a **34px** gap: the reservation is spent from the header's own `p-6`
 *   edge, so `gap = 20.4 + pr - 13.6 - 13.6`, and one more spacing step buys the
 *   missing 6.8px (40.8px). A step on the scale rather than an arbitrary
 *   `pr-[46px]` that would hit 39.8 exactly — the whole treatment rides the
 *   `--spacing` density knob, and being a pixel over "~40px" is worth keeping
 *   that.
 */
export const ATTRIBUTE_DRAWER_HEADER_ROW =
  '-mt-[13px] flex justify-between gap-3 pr-14';

export function AttributeDrawerDelete({
  recordName,
  entityLabel,
  description,
  onConfirm,
  disabledReason
}: {
  /** Named in the confirmation, so it says which record is going. */
  recordName: string;
  /** What the record is called in the button and the confirmation — `item`, `contact`. */
  entityLabel: string;
  /** The consequence, in the record's own terms. */
  description: string;
  /**
   * The host's **real** delete, run once the reader has confirmed.
   *
   * It must also leave the drawer closed: hosts clear their selection
   * afterwards, and a null selection with the drawer still open re-opens it in
   * *Add* mode. It may reject — the popover re-enables its buttons and leaves
   * itself open, and the caller reports the failure (see
   * `DeleteConfirmPopover`).
   */
  onConfirm: () => void | Promise<void>;
  /**
   * When set, the control is disabled and this explains why on hover, instead of
   * offering a delete the server will refuse (VALE-1760). The host knows the
   * refusal in advance; letting the reader click and meet a toast is the shape
   * this replaces.
   */
  disabledReason?: string | null;
}) {
  if (disabledReason) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {/* A disabled button still needs a hoverable wrapper for the tooltip. */}
          <span>
            <Button
              type="button"
              variant="ghost"
              size="target-32"
              density="micro"
              tone="muted-danger"
              gap="standard"
              pad="sm"
              weight="normal"
              className="shrink-0"
              disabled
            >
              <Trash2Icon className="h-3.5 w-3.5 shrink-0" />
              Delete {entityLabel}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          {disabledReason}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <DeleteConfirmPopover
      trigger={
        <Button
          type="button"
          variant="ghost"
          size="target-32"
          density="micro"
          tone="muted-danger"
          gap="standard"
          pad="sm"
          weight="normal"
          className="shrink-0"
        >
          <Trash2Icon className="h-3.5 w-3.5 shrink-0" />
          Delete {entityLabel}
        </Button>
      }
      title={
        <>
          Delete <span className="font-medium">{recordName}</span>?
        </>
      }
      description={description}
      onConfirm={onConfirm}
    />
  );
}
